import { useState } from 'react';
import { Button, Badge, Spinner } from '../../ui/components';
import { Bot, ChevronRight, X, Sparkles, AlertCircle, CircleCheck, Info } from 'lucide-react';
import { OWEN_CHARACTER } from '../../config/owenAI';
import { LK_FEATURES } from '../../config/features';

/**
 * FloatingOwenAssistant - Proactive AI analysis that runs on demand
 * Analyzes current context and provides insights automatically
 */
const FloatingOwenAssistant = ({ 
  context = 'general',
  contextData = null, // Data to analyze (loan details, application data, etc.)
  onRunAnalysis = null, // Callback to get AI analysis
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);

  const handleRunAnalysis = async () => {
    setIsAnalyzing(true);
    
    try {
      // If custom analysis function provided, use it
      if (onRunAnalysis) {
        const result = await onRunAnalysis(context, contextData);
        setAnalysis(result);
      } else {
        // Default analysis based on context
        const result = await generateDefaultAnalysis(context, contextData);
        setAnalysis(result);
      }
      
      setLastAnalyzedAt(new Date());
    } catch (error) {
      console.error('Analysis error:', error);
      setAnalysis({
        summary: 'Unable to complete analysis',
        insights: [],
        recommendations: [],
        alerts: [{ type: 'error', message: 'Analysis failed. Please try again.' }],
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateDefaultAnalysis = async (ctx, data) => {
    // Simulate AI analysis (replace with actual API call)
    await new Promise(resolve => setTimeout(resolve, 2000));

    const analyses = {
      'borrower-dashboard': {
        summary: 'Your mortgage application portfolio looks healthy.',
        insights: [
          { type: 'success', text: 'All active applications are progressing on schedule' },
          { type: 'info', text: 'Average readiness score is above 75% - excellent preparation' },
          { type: 'warning', text: '1 application needs document upload to proceed' },
        ],
        recommendations: [
          'Upload missing bank statements for Application #2 to improve readiness score',
          'Consider starting pre-approval process for your highest-scoring application',
          'Review and update employment information if any changes occurred recently',
        ],
        alerts: [],
      },
      'underwriter-dashboard': {
        summary: 'Your pipeline is well-balanced with manageable workload.',
        insights: [
          { type: 'success', text: 'Current capacity utilization is optimal at 68%' },
          { type: 'info', text: '3 applications ready for conditional clearance' },
          { type: 'warning', text: '2 applications have pending conditions over 5 days old' },
        ],
        recommendations: [
          'Prioritize Application DEMO001 - all conditions met, ready to clear',
          'Follow up on pending income verification for Application DEMO002',
          'Consider claiming 1-2 high-score applications from unassigned queue',
        ],
        alerts: [
          { type: 'warning', message: 'Application DEMO003 has DTI ratio at 48% - near guideline limit' },
        ],
      },
      'application-form': {
        summary: 'Form completion analysis based on current progress.',
        insights: [
          { type: 'info', text: 'Income section is 80% complete' },
          { type: 'warning', text: 'Asset documentation may need additional verification' },
          { type: 'success', text: 'Credit profile meets conventional loan requirements' },
        ],
        recommendations: [
          'Add co-borrower income to improve DTI ratio from 42% to 35%',
          'Consider documenting additional liquid assets to strengthen application',
          'Review property tax estimates - they seem high for this area',
        ],
        alerts: [],
      },
      'general': {
        summary: 'Ready to analyze your mortgage application context.',
        insights: [
          { type: 'info', text: 'Click "Run AI Analysis" to get personalized insights' },
        ],
        recommendations: [
          'AI will analyze your current page and provide specific recommendations',
          'Analysis considers your loan details, documentation, and market conditions',
        ],
        alerts: [],
      },
    };

    return analyses[ctx] || analyses.general;
  };

  if (!LK_FEATURES.aiAnalysis) return null;

  // Minimized tab
  if (isMinimized) {
    return (
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1040,
          cursor: 'pointer',
        }}
        onClick={() => {
          setIsMinimized(false);
          setIsExpanded(true);
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--lk-space-2)',
            padding: 'var(--lk-space-3) var(--lk-space-3)',
            background: 'var(--lk-primary-500)',
            color: 'white',
            borderTopLeftRadius: 'var(--lk-radius-md)',
            borderBottomLeftRadius: 'var(--lk-radius-md)',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
        >
          <Bot size={20} />
          <span style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>AI Analysis</span>
          <Sparkles size={16} />
        </div>
      </div>
    );
  }

  // Expanded panel
  return (
    <div
      style={{
        position: 'fixed',
        right: isExpanded ? 0 : -380,
        top: 0,
        height: '100vh',
        width: isExpanded ? '420px' : '60px',
        transition: 'all 0.3s ease',
        zIndex: 1040,
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {/* Toggle button */}
      <div
        style={{
          width: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--lk-primary-500)',
          color: 'white',
          cursor: 'pointer',
          borderTopLeftRadius: isExpanded ? 0 : 'var(--lk-radius-md)',
          borderBottomLeftRadius: isExpanded ? 0 : 'var(--lk-radius-md)',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
        }}
        onClick={() => {
          if (isExpanded) {
            setIsExpanded(false);
            setIsMinimized(true);
          } else {
            setIsExpanded(true);
          }
        }}
      >
        {isExpanded ? (
          <ChevronRight size={24} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--lk-space-2)', padding: 'var(--lk-space-4) 0' }}>
            <Bot size={24} />
            <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>AI</span>
            </div>
          </div>
        )}
      </div>

      {/* Main panel */}
      <div
        style={{
          width: '380px',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.1)',
          background: 'white',
          borderLeft: '1px solid var(--lk-border-subtle)',
        }}
      >
        {/* Header */}
        <div style={{ background: 'var(--lk-gray-900)', color: 'white', padding: 'var(--lk-space-3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
            <img src="/assets/owen-avatar.png" alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 'var(--lk-text-sm)' }}>{OWEN_CHARACTER.name} AI</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>Intelligent Analysis</div>
            </div>
          </div>
          <button
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 4 }}
            onClick={() => { setIsExpanded(false); setIsMinimized(true); }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Context indicator */}
        <div style={{ background: 'var(--lk-gray-50)', borderBottom: '1px solid var(--lk-border-subtle)', padding: 'var(--lk-space-2) var(--lk-space-3)' }}>
          <span style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Sparkles size={14} />
            Analyzing: <span style={{ fontWeight: 600 }}>{context.replace(/-/g, ' ')}</span>
          </span>
        </div>

        {/* Run Analysis Button */}
        <div style={{ padding: 'var(--lk-space-3)', borderBottom: '1px solid var(--lk-border-subtle)' }}>
          <Button
            variant="primary"
            icon={Sparkles}
            loading={isAnalyzing}
            onClick={handleRunAnalysis}
            style={{ width: '100%' }}
          >
            Run AI Analysis
          </Button>
          {lastAnalyzedAt && (
            <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', textAlign: 'center', marginTop: 'var(--lk-space-2)' }}>
              Last analyzed: {lastAnalyzedAt.toLocaleTimeString()}
            </div>
          )}
        </div>

        {/* Analysis Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--lk-space-3)' }}>
          {!analysis ? (
            <div style={{ textAlign: 'center', padding: 'var(--lk-space-8) 0' }}>
              <Bot size={48} color="var(--lk-text-quaternary)" style={{ marginBottom: 'var(--lk-space-3)' }} />
              <p style={{ color: 'var(--lk-text-tertiary)' }}>
                Click "Run AI Analysis" to get intelligent insights about your current context.
              </p>
              <p style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-quaternary)' }}>
                AI will analyze what you're looking at and provide specific recommendations.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
              {/* Summary */}
              <div>
                <h6 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-2)', display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                  <Sparkles size={18} color="var(--lk-primary-500)" />
                  Summary
                </h6>
                <p style={{ fontSize: 'var(--lk-text-sm)', margin: 0 }}>{analysis.summary}</p>
              </div>

              {/* Alerts */}
              {analysis.alerts && analysis.alerts.length > 0 && (
                <div>
                  <h6 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-2)', display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                    <AlertCircle size={18} color="var(--lk-warning-500)" />
                    Alerts
                  </h6>
                  {analysis.alerts.map((alert, idx) => (
                    <div key={idx} style={{ padding: 'var(--lk-space-2) var(--lk-space-3)', fontSize: 'var(--lk-text-sm)', borderRadius: 'var(--lk-radius-md)', marginBottom: 'var(--lk-space-2)', background: alert.type === 'error' ? 'var(--lk-danger-50)' : 'var(--lk-warning-50)', color: alert.type === 'error' ? 'var(--lk-danger-600)' : 'var(--lk-warning-600)' }}>
                      {alert.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Insights */}
              {analysis.insights && analysis.insights.length > 0 && (
                <div>
                  <h6 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-2)', display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                    <Info size={18} color="var(--lk-primary-500)" />
                    Key Insights
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
                    {analysis.insights.map((insight, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 'var(--lk-space-2)', alignItems: 'flex-start' }}>
                        {insight.type === 'success' && <CircleCheck size={16} color="var(--lk-success-500)" style={{ marginTop: 2, flexShrink: 0 }} />}
                        {insight.type === 'warning' && <AlertCircle size={16} color="var(--lk-warning-500)" style={{ marginTop: 2, flexShrink: 0 }} />}
                        {insight.type === 'info' && <Info size={16} color="var(--lk-primary-500)" style={{ marginTop: 2, flexShrink: 0 }} />}
                        <span style={{ fontSize: 'var(--lk-text-sm)', flex: 1 }}>{insight.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <div>
                  <h6 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-2)', display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                    <CircleCheck size={18} color="var(--lk-success-500)" />
                    Recommendations
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
                    {analysis.recommendations.map((rec, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 'var(--lk-space-2)', alignItems: 'flex-start' }}>
                        <Badge variant="success" style={{ marginTop: 2 }}>{idx + 1}</Badge>
                        <span style={{ fontSize: 'var(--lk-text-sm)', flex: 1 }}>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid var(--lk-border-subtle)', padding: 'var(--lk-space-3)', background: 'var(--lk-gray-50)' }}>
          <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Sparkles size={12} />
            AI analysis is based on current context and data
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingOwenAssistant;
